import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  useUser,
  useTrazadores,
  useEquipment,
  useDetector,
} from "@services/servicesGenericsTables";
import { useLaboratory } from "@services/servicesGenericsTables/useLaboratory";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useTypesUnits } from "@services/servicesGenericsTables/useTypesUnits";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import {
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import ResultsTablePuAmCMGenie from "./ResultsTablePuAmCMGenie";
import FileUploadGenie, { parseRptFileGenie } from "./fileUploadGenie";
import { ResultsTypes } from "../../../../../types/samples.types";
import { useCalibrationTritium } from "@app/pages/calibrations/utils/useCalibration";
import { useTrazadorSelector } from "@app/pages/samples/hook/useTrazadorSelector";
import { useFileParser } from "@app/pages/samples/hook/useFileParser";
import { 
  calculateResults, 
  buildResultData 
} from "@app/pages/samples/utils/calculationHelpers";
import { SOURCE_APP } from "@app/pages/samples/utils/calculationConstants";

const CalculationPuAmCM = () => {
  const { setValue, getValues, watch } = useFormContext();
  const { userList } = useUser();
  const { laboratoriesList } = useLaboratory();
  const { trazadoresList, fetchAll: fetchTrazadores } = useTrazadores();
  const { fechtCalibrationTritiumByAnalysisType, calibrationTritiumFiltered } =
    useCalibrationTritium();
  const { equipmentList } = useEquipment();
  const { detectorsList } = useDetector();
  
  const { parseAndMapData, mapDataToFormFields } = useFileParser();
  
  // Estado local para mantener los valores parseados del archivo
  const [parsedFileData, setParsedFileData] = useState<{
    totalActivity?: number;
    totalUncertainty?: number;
    totalLid?: number;
    efficiencieTotal?: number;
    counting_detector?: any[];
  }>({});


  // Cargar datos iniciales
  useEffect(() => {
    const fetchAllData = async () => {
      await fechtCalibrationTritiumByAnalysisType(
        getValues("typeAnalysis")?.id || 0
      );
      await fetchTrazadores();
    };

    fetchAllData();
  }, []);

  const {
    currentCalculation,
    loadingResults,
    importFileCalculation,
    saveResultCalculate,
  } = useAnalysis();
  const { typesUnitsis } = useTypesUnits();
  const filteredCalculation = useFilteredProcedures("CAL");
  const codeAnalysis = getValues("typeAnalysis")?.codigo;

  const isotopeType = getValues("isotope_type");
  
  // Usar el hook personalizado para seleccionar el trazador
  const selectedTrazador = useTrazadorSelector(isotopeType, trazadoresList);
  
  const [fetchTrigger, setFetchTrigger] = useState(false);

  // Actualizar trazador en el formulario cuando cambia
  useEffect(() => {
    if (selectedTrazador) {
      setValue("trazador", selectedTrazador);
    }
  }, [selectedTrazador, setValue]);

  const countingDetector = watch("counting_detector");
  
  const sortedListEfficiencie = useMemo(() => {
    const countingDateObj = new Date(getValues("counting_date"));
    const countingDetectors = countingDetector;

    const allowedDetectorIds = countingDetectors
      ? new Set(countingDetectors.map((det) => det.name))
      : new Set();

    if (calibrationTritiumFiltered === undefined) {
      return [];
    }

    const filterCalibration = calibrationTritiumFiltered.filter((item) => {
      if (!item.date_detector) return false;
      const itemDate = new Date(item.date_detector);
      return itemDate <= countingDateObj && allowedDetectorIds.has(item.detector?.name);
    });
    
    return filterCalibration || [];
  }, [calibrationTritiumFiltered, countingDetector]);

  const efficiencieTotal = watch("efficiencieTotal");
  const efficiencieMedia = sortedListEfficiencie?.[0]?.mean_efficiency || 0;
  
  const efficiencieMediaNumber = useMemo(() => {
    return efficiencieMedia !== null ? Number(efficiencieMedia) : undefined;
  }, [efficiencieMedia]);

  // Inicializar source_app_calculation_info
  useEffect(() => {
    setValue("source_app_calculation_info", SOURCE_APP.GENIE);
  }, [setValue]);

  // Procesar archivo cuando se carga un cálculo existente
  // Para cálculos nuevos, el archivo se procesa directamente en FileUploadGenie.onFileChange
  useEffect(() => {
    const nameFile = getValues("source_app_calculation_info_file");
    
    const processFileData = async (fileName: string) => {
      const response = await importFileCalculation(fileName, "TO_FORM");
      const isotope_code = watch("isotope_code");

      if (!response) return;
      
      const parsedData = parseRptFileGenie(response, isotope_code);
      const mappedData = parseAndMapData(parsedData);
      const formFields = mapDataToFormFields(
        parsedData,
        mappedData.equipment,
        mappedData.detector
      );

      // Guardar los valores parseados en el estado local
      setParsedFileData({
        totalActivity: parsedData.totalActivity,
        totalUncertainty: parsedData.totalUncertainty,
        totalLid: parsedData.totalLid,
        efficiencieTotal: parsedData.efficiencieTotal,
        counting_detector: mappedData.detector ? [mappedData.detector] : [],
      });

      // Aplicar todos los campos al formulario
      Object.entries(formFields).forEach(([key, value]) => {
        setValue(key, value);
      });
    };
    
    // Solo procesar archivo cuando hay un cálculo existente (con ID)
    // ya que fue guardado previamente
    if (currentCalculation.id && nameFile) {
      processFileData(nameFile);
    } else if (selectedTrazador && !currentCalculation.id && !nameFile) {
      // Establecer trazador solo si es cálculo nuevo, sin archivo
      setValue("trazador", selectedTrazador);
    }
  }, [currentCalculation.id, fetchTrigger]); 

  // Re-aplicar valores parseados al formulario cuando se pierden (por ej. después de actualizar contaje)
  useEffect(() => {
    if (Object.keys(parsedFileData).length > 0) {
      const currentTotalActivity = getValues("totalActivity");
      const currentTotalUncertainty = getValues("totalUncertainty");
      const currentTotalLid = getValues("totalLid");
      const currentEfficiencieTotal = getValues("efficiencieTotal");
      const currentCountingDetector = getValues("counting_detector");
      
      // Solo re-aplicar si los valores se perdieron
      if (parsedFileData.totalActivity !== undefined && !currentTotalActivity) {
        setValue("totalActivity", parsedFileData.totalActivity);
      }
      if (parsedFileData.totalUncertainty !== undefined && !currentTotalUncertainty) {
        setValue("totalUncertainty", parsedFileData.totalUncertainty);
      }
      if (parsedFileData.totalLid !== undefined && !currentTotalLid) {
        setValue("totalLid", parsedFileData.totalLid);
      }
      if (parsedFileData.efficiencieTotal !== undefined && !currentEfficiencieTotal) {
        setValue("efficiencieTotal", parsedFileData.efficiencieTotal);
      }
      if (parsedFileData.counting_detector && parsedFileData.counting_detector.length > 0 && (!currentCountingDetector || currentCountingDetector.length === 0)) {
        setValue("counting_detector", parsedFileData.counting_detector);
      }
    }
  }, [currentCalculation, parsedFileData, watch("counting_detector")]);

  const textButtonCalculation = "Calcular";
  
  // Usar watch para reactividad en la UI
  const totalActivity = watch("totalActivity");
  const totalUncertainty = watch("totalUncertainty");
  const totalLid = watch("totalLid");

  // Calcular resultados usando valores del estado local o formulario
  const calculatedResults = useMemo(() => {
    // Priorizar valores del estado local (archivo parseado)
    const currentActivity = parsedFileData.totalActivity ?? totalActivity;
    const currentUncertainty = parsedFileData.totalUncertainty ?? totalUncertainty;
    const currentLid = parsedFileData.totalLid ?? totalLid;
    const currentEfficiency = parsedFileData.efficiencieTotal ?? efficiencieTotal;
    
    return calculateResults(
      currentActivity,
      currentUncertainty,
      currentLid,
      currentEfficiency,
      efficiencieMediaNumber
    );
  }, [parsedFileData, totalActivity, totalUncertainty, totalLid, efficiencieTotal, efficiencieMediaNumber]);

  const handleCalculate = async () => {
    // Usar los valores del estado local (del archivo parseado) o del formulario
    const currentTotalActivity = parsedFileData.totalActivity ?? getValues("totalActivity");
    const currentTotalUncertainty = parsedFileData.totalUncertainty ?? getValues("totalUncertainty");
    const currentTotalLid = parsedFileData.totalLid ?? getValues("totalLid");
    const currentEfficiencieTotal = parsedFileData.efficiencieTotal ?? getValues("efficiencieTotal");
    
    const resultData = buildResultData(
      {
        to_report: watch("to_report"),
        totalActivity: currentTotalActivity,
        totalUncertainty: currentTotalUncertainty,
        totalLid: currentTotalLid,
      },
      {
        total: currentEfficiencieTotal,
        media: efficiencieMediaNumber,
      }
    );

    await saveResultCalculate(codeAnalysis as string, resultData);
    setFetchTrigger((prev) => !prev);
  };

  return (
    <>
      {currentCalculation.index !== undefined && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>

          <ResponsiveFormContainer columns={3}>
            <MTextField
              key={JSON.stringify(getValues("amount_sample"))}
              label="Cantidad de muestra"
              name="calculation_amount_sample"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MDatePicker
              key="analysis_start_date_time"
              name="analysis_start_date_time"
              pickerType="datetime"
              label="Fecha inicio análisis"
              disabled
            />
            <MTextField
              key={JSON.stringify(getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              name="analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />

            <MAutocomplete
              key={JSON.stringify(getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
            />
            <MAutocomplete
              name="trazador"
              options={trazadoresList}
              label="Seleccione un Trazador"
              getOptionLabel={(option) => `${option.isotopo} - ${option.trazador}`} 
            />
            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
              // minDate={dayjs(watch("calculation_counting_date"))}
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos del fichero Genie:"}
            </Typography>
          </Grid>
          <FileUploadGenie onDataParsed={setParsedFileData} />
          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos obtenidos:"}</Typography>
          </Grid>
          <ResponsiveFormContainer columns={3}>
            <MTextField
              name="calculation_date_countig_file"
              label="Fecha de contaje"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_equipment_file_generic"
              label="Equipo"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_detectors_generic"
              label="Detector"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_time_count"
              label="Tiempo de contaje (s)"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_volume"
              label="Volumen de trazador (ml)"
              disabled
              fullWidth
            />

            <MTextField
              name="calibration_source_certificate"
              label="Certificado del trazador"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_background"
              label="Fondo"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_calibration"
              label="Calibración"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_library"
              label="Librería"
              disabled
              fullWidth
            />

            <MTextField
              name="calculation_sample"
              label="Espectro Muestra"
              disabled
              fullWidth
            />
          </ResponsiveFormContainer>

          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!watch("idResultPreload")}
              />
            </Box>
          )}

          <ResultsTablePuAmCMGenie
            totalActivity={calculatedResults.totalActivity}
            totalUncertainty={calculatedResults.totalUncertainty}
            totalLid={calculatedResults.totalLid}
            efficiencieTotal={calculatedResults.efficiencieTotal}
            efficiencieMedia={calculatedResults.efficiencieMedia}
            fetchTrigger={fetchTrigger}
          />
        </>
      )}
    </>
  );
};

export default CalculationPuAmCM;